// @packages
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import TextField from '@material-ui/core/TextField';
import classNames from 'classnames';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

// @scripts
import CtrlIcon from './icon';
import { config } from '../../../../config';

import {
    MAX_DATE_VALUE,
    MIN_DATE_VALUE,
    format,
    isDate,
    isEmail,
    isName,
    isNumber,
    isPhone,
    isZip,
    removeTime,
    toShortDateString
} from '../../../../helpers';

// @styles
import styles from './styles';
import { DEFAULT_FIELD_VARIANT } from '../../../../styles/globals';

// @constants
const EMPTY_ERROR = ' ';

const inputTypeOption = {
    DATE: 'date',
    EMAIL: 'email',
    NAME: 'name',
    NUMERIC: 'numeric',
    PASSWORD: 'password',
    PHONE: 'phone',
    TEXT: 'text',
    ZIP: 'zip'
};

const transformType = {
    LOWER: 'lowercase',
    NONE: 'none',
    UPPER: 'uppercase'
};

const CtrlTextField = ({
    autoFocus,
    className,
    classes,
    dateFormat,
    decimals,
    disabled,
    expectedValue,
    helpText,
    icon,
    id,
    label,
    maxLength,
    maxValue,
    minLength,
    minValue,
    name,
    onBlur,
    onChange,
    onEnter,
    onFocus,
    onIconClick,
    placeholder,
    regexPattern,
    required,
    showErrors,
    textTransform,
    type,
    value,
    variant,
    visible
}) => {
    

    const [showPassword, setShowPassword] = useState(false);
    const textFieldClass = classNames(
        className,
        classes.textField
    );
    if (!visible) {
        return null;
    }

    const allowNegatives = minValue < 0;
    const inputType = type === inputTypeOption.PASSWORD && !showPassword
        ? inputTypeOption.PASSWORD
        : inputTypeOption.TEXT;

    const handlePwdIconOnClick = () => setShowPassword((previousState) => ({
        showPassword: !previousState.showPassword
    }));

    const getInputProps = () => {
        let iconName = icon;
        let iconClick = onIconClick;

        if (helpText && !icon) {
            iconName = 'feedback';
        }

        if (type === inputTypeOption.PASSWORD) {
            iconName = showPassword ? 'visibility' : 'visibility_off';
            iconClick = handlePwdIconOnClick;
        }

        return iconName ? {
            endAdornment: (
                <CtrlIcon
                    helpText={helpText}
                    icon={iconName}
                    id={`${id}-icon`}
                    onIconClick={iconClick}
                />
            )
        } : {};
    };

    const getPlaceholder = () => {
        if (placeholder) {
            return placeholder;
        }

        switch (type) {
            case inputTypeOption.DATE:
                return dateFormat.toLowerCase();
            case inputTypeOption.EMAIL:
                return config.text.textFieldPlaceHolders.email;
            case inputTypeOption.PHONE:
                return config.text.textFieldPlaceHolders.phone;
            case inputTypeOption.ZIP:
                return config.text.textFieldPlaceHolders.zip;
            default:
                return placeholder;
        }
    };

    const getMinValue = () => {
        switch (type) {
            case inputTypeOption.DATE:
                const date = minValue !== null
                    ? minValue
                    : MIN_DATE_VALUE;
                return removeTime(date);
            case inputTypeOption.NUMERIC:
                return minValue !== null
                    ? minValue
                    : Number.MIN_SAFE_INTEGER;
            default:
                return null;
        }
    };

    const getMaxValue = () => {
        switch (type) {
            case inputTypeOption.DATE:
                const date = maxValue !== null
                    ? maxValue
                    : MAX_DATE_VALUE;
                return removeTime(date);
            case inputTypeOption.NUMERIC:
                return maxValue !== null
                    ? maxValue
                    : Number.MAX_SAFE_INTEGER;
            default:
                return null;
        }
    };

    const parseValue = (value) => {
        switch (type) {
            case inputTypeOption.DATE:
                const date = moment(value, dateFormat).toDate();
                return removeTime(date);
            case inputTypeOption.NUMERIC:
                return parseFloat(value);
            default:
                return value;
        }
    };

    const getErrorMsg = (value) => {
        if (required && !value) {
            return config.text.validations.required;
        }

        if (expectedValue !== undefined && (expectedValue || '') !== (value || '')) {
            return config.text.validations.verification;
        }

        if (!value) {
            return EMPTY_ERROR;
        }

        if (value.toString().length < minLength) {
            return format(config.text.validations.minCharacters, minLength);
        }

        if (type === inputTypeOption.EMAIL && !isEmail(value)) {
            return config.text.validations.email;
        }

        if (type === inputTypeOption.PHONE && !isPhone(value)) {
            return config.text.validations.phone;
        }

        if (type === inputTypeOption.ZIP && !isZip(value)) {
            return config.text.validations.zip;
        }

        if (type === inputTypeOption.DATE) {
            if (!isDate(value, dateFormat)) {
                return config.text.validations.date;
            }
            if (parseValue(value) < getMinValue()) {
                const strDate = toShortDateString(getMinValue());
                return format(config.text.validations.minValue, strDate);
            }
            if (parseValue(value) > getMaxValue()) {
                const strDate = toShortDateString(getMaxValue());
                return format(config.text.validations.maxValue, strDate);
            }
        }

        if (type === inputTypeOption.NUMERIC) {
            if (!isNumber(value, decimals, allowNegatives)) {
                return config.text.validations.number;
            }
            if (parseValue(value) < getMinValue()) {
                return format(config.text.validations.minValue, getMinValue());
            }
            if (parseValue(value) > getMaxValue()) {
                return format(config.text.validations.maxValue, getMaxValue());
            }
        }

        return EMPTY_ERROR;
    };

    const getTransformedText = (value) => {
        switch (textTransform) {
            case transformType.UPPER:
                return value.toUpperCase();
            case transformType.LOWER:
                return value.toLowerCase();
            default:
                return value;
        }
    };

    const handleOnBlur = () => {
        if (value !== null && value !== '' && type === inputTypeOption.NUMERIC && isNumber(value, decimals)) {
            const decimalConverted = parseFloat(value).toFixed(decimals);
            onBlur({
                name: name || id,
                value: decimalConverted
            });
        }
    };

    const handleInputOnChange = (evt) => {
        const { value } = evt.target;

        if (!onChange) {
            return;
        }

        if (value.length > maxLength) {
            return;
        }

        if (value !== '' && type === inputTypeOption.NAME && !isName(value)) {
            return;
        }

        if (value !== ''
            && type === inputTypeOption.NUMERIC
            && !isNumber(value, decimals, allowNegatives)) {
            if (!isNumber(`${value}0`, decimals, allowNegatives)) {
                return;
            }
        }

        if (value !== ''
            && regexPattern
            && !regexPattern.test(value)) {
            return;
        }

        onChange({
            isValid: getErrorMsg(value) === EMPTY_ERROR,
            name: name || id,
            value: getTransformedText(value)
        });
    };

    const handleInputOnKeyPress = (evt) => {
        if (evt.key === 'Enter' && onEnter) {
            onEnter();
        }
    };

    const errorMsg = getErrorMsg(value);
    const hasError = showErrors && errorMsg !== EMPTY_ERROR;
    const helperText = hasError ? errorMsg : EMPTY_ERROR;

    return (
        <TextField
            InputProps={getInputProps()}
            autoComplete="off"
            autoFocus={autoFocus}
            className={textFieldClass}
            disabled={disabled}
            error={hasError}
            fullWidth
            helperText={helperText}
            id={id}
            label={label}
            onBlur={handleOnBlur}
            onChange={handleInputOnChange}
            onFocus={onFocus}
            onKeyPress={handleInputOnKeyPress}
            placeholder={getPlaceholder()}
            type={inputType}
            value={value || ''}
            variant={variant}
        />
    );
};

CtrlTextField.propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    classes: PropTypes.object.isRequired,
    dateFormat: PropTypes.string,
    decimals: PropTypes.number,
    disabled: PropTypes.bool,
    expectedValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    helpText: PropTypes.string,
    icon: PropTypes.string,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    maxValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]),
    minLength: PropTypes.number,
    minValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]),
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onEnter: PropTypes.func,
    onFocus: PropTypes.func,
    onIconClick: PropTypes.func,
    placeholder: PropTypes.string,
    regexPattern: PropTypes.object,
    required: PropTypes.bool,
    showErrors: PropTypes.bool,
    textTransform: PropTypes.oneOf([...Object.values(transformType)]),
    type: PropTypes.oneOf([...Object.values(inputTypeOption)]),
    value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
    visible: PropTypes.bool
};

CtrlTextField.defaultProps = {
    autoFocus: false,
    className: null,
    dateFormat: 'MM/DD/YYYY',
    decimals: 0,
    disabled: false,
    expectedValue: undefined,
    helpText: null,
    icon: null,
    label: null,
    maxLength: Number.MAX_SAFE_INTEGER,
    maxValue: null,
    minLength: 0,
    minValue: null,
    name: null,
    onBlur: null,
    onChange: null,
    onEnter: null,
    onFocus: null,
    onIconClick: null,
    placeholder: null,
    regexPattern: null,
    required: false,
    showErrors: true,
    textTransform: 'none',
    type: 'text',
    value: null,
    variant: DEFAULT_FIELD_VARIANT,
    visible: true
};

export default withStyles(styles)(CtrlTextField);
